import React from 'react';
import { Heart, Target, Users, Lightbulb, TrendingUp, Award } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  const values = [
    {
      icon: Heart,
      title: 'Passion & Purpose',
      description: 'Every solution we create is driven by a deep commitment to helping all learners succeed, regardless of their unique challenges.'
    },
    {
      icon: Target,
      title: 'Evidence-Based',
      description: 'Our resources are grounded in current research and best practices in special education and autism support.'
    },
    {
      icon: Users,
      title: 'Community Partnership',
      description: 'We collaborate with educators, families, and specialists to understand real needs and create meaningful solutions.'
    },
    {
      icon: Lightbulb,
      title: 'Innovation',
      description: 'Continuously exploring new approaches and technologies to advance autism research and educational methods.'
    }
  ];

  return (
    <div className="min-h-screen py-16">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-20">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6 text-foreground">
            About <span className="gradient-text">All NextGen Networking Solutions</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            A passion project dedicated to advancing education for autism, SPED, and all diverse learners.
          </p>
        </div>

        {/* Our Story Section */}
        <section className="mb-24">
          <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl p-10 sm:p-16 border-2 border-border">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center">
                  <Heart className="h-6 w-6 text-accent" />
                </div>
                <h2 className="text-3xl font-bold text-foreground">Our Story</h2>
              </div>
              <div className="space-y-6 text-lg text-foreground/80 leading-relaxed">
                <p>
                  <strong className="text-foreground">All NextGen Networking Solutions</strong> was born from a vision to connect providers with next-generation solutions across various fields. What began as a shared dream took on new meaning through unexpected life changes.
                </p>
                <p>
                  After a divorce, I found myself rebuilding not just a company, but a purpose. The original vision transformed into something more personal and profound—a commitment to advancing education and support for children with autism, SPED needs, and all diverse learning styles.
                </p>
                <p>
                  Today, <strong className="text-foreground">Ann Solutions</strong> stands as a testament to resilience and renewed purpose. We started with what we could do immediately: creating quality educational resources and tracing worksheets that make a real difference for educators and families. But our vision extends far beyond—building partnerships in autism research and creating platforms that connect providers, educators, and families.
                </p>
                <p className="text-accent font-semibold">
                  This is more than a business. It's a mission to ensure every child, regardless of their learning differences, has access to the tools and support they need to thrive.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="mb-24">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="border-2">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center mb-6">
                  <Target className="h-7 w-7 text-primary" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-foreground">Our Mission</h3>
                <p className="text-muted-foreground leading-relaxed text-lg">
                  To provide high-quality, accessible educational resources for children with autism, SPED needs, and diverse learning styles, while building a foundation for future research and community connection.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-lg bg-accent/10 flex items-center justify-center mb-6">
                  <TrendingUp className="h-7 w-7 text-accent" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-foreground">Our Vision</h3>
                <p className="text-muted-foreground leading-relaxed text-lg">
                  To become a trusted hub connecting autism researchers, educational solution providers, families, and educators—advancing the field through innovation, collaboration, and evidence-based practices.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Core Values */}
        <section className="mb-24">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-foreground">
              Our <span className="gradient-text">Core Values</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              The principles that guide every decision we make and every solution we create.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="border-2 hover:border-primary/50 transition-all card-hover">
                  <CardContent className="pt-8 pb-6 px-8">
                    <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center mb-6">
                      <Icon className="h-7 w-7 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold mb-3 text-foreground">{value.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Current Focus */}
        <section className="mb-24">
          <div className="bg-white rounded-2xl p-10 sm:p-16 border-2 border-border max-w-4xl mx-auto">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Award className="h-6 w-6 text-primary" />
              </div>
              <h2 className="text-3xl font-bold text-foreground">What We Do Today</h2>
            </div>
            <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
              <p>
                Currently, we specialize in creating <strong className="text-foreground">high-quality educational resources</strong> for children with diverse learning needs. Our tracing worksheets and workbooks are designed specifically for autism, SPED, ESL, and early elementary students.
              </p>
              <p>
                Available through <strong className="text-foreground">Teachers Pay Teachers</strong> and <strong className="text-foreground">Amazon</strong>, our materials combine evidence-based approaches with practical, easy-to-use formats that educators and families trust.
              </p>
              <p className="text-primary font-semibold">
                Every product we create today builds the foundation for our larger vision—a connected ecosystem of autism research, advanced educational solutions, and community support.
              </p>
            </div>
          </div>
        </section>

        {/* Commitment */}
        <section>
          <div className="bg-primary text-white rounded-2xl p-10 sm:p-16 relative overflow-hidden">
            <div className="absolute inset-0 opacity-30" />
            <div className="max-w-3xl mx-auto text-center relative z-10">
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Our Commitment to You
              </h2>
              <p className="text-xl text-white/90 leading-relaxed">
                We're committed to continuous growth—listening to your needs, staying current with research, and always striving to create better solutions. Whether you're an educator, a parent, or a specialist, your feedback and partnership help us build a better future for all learners.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default About;